import React from 'react';
import getConfig from 'next/config';
import { gql, useQuery } from '@apollo/client';
import Header, { Breadcrumbs } from '@domain-group/fe-co-header';
import { ContactProfileFooterWithFragment } from '..';
import { ProfilePlaceholder as Placeholder, StatTiles } from '../shared';
import getStatTilesVariant from './get-stat-tiles-variant';
import AgentProfileAgencyBanner from '../agent-profile-agency-banner/agent-profile-agency-banner';
import AgentProfileOverview from '../agent-profile-overview/agent-profile-overview';
import { mapContactBreadcrumbs, mapHeaderData } from '../header/utils';
import { skipLinkName } from '../../helper/accessibility-utils';
import { GetAgentProfile2026Query } from '../../generated/graphql';
import { COMPONENT_IDS } from '../../constants';
import buildAgencyProfileUrl from '../../utils/build-agency-profile-url';
import * as styles from './agent-profile-2026.style';

const { publicRuntimeConfig } = getConfig();

/* eslint-disable react/no-unused-prop-types -- not consumed on this skeleton yet, plugged in as later sections land */
export interface AgentProfile2026Props {
  contactId: number;
  requestAppraisalUrl: string;
  mixpanelHasInitialized: boolean;
  isEmbeddedApp?: boolean;
  starRatingsEnabled?: boolean;
  hideStarRatings?: boolean;
  user?: { userId?: string };
}
/* eslint-enable react/no-unused-prop-types */

// The legacy AGENT_PROFILE_QUERY (in [agentSlug].tsx) already ran during
// getServerSideProps and populated the Apollo cache - this query is this
// page's own equivalent, growing to cover each section as it lands (currently
// breadcrumbs/footer identifiers, the identity block, and the Section_Overview
// stat tiles). The header here is always a plain, solid Header (unlike the
// legacy ContactProfileHeaderWithFragment, which goes transparent for
// platinum agents to sit over the Hero photo - there's no photo behind it
// yet on this skeleton).
export const AGENT_PROFILE_2026_QUERY = gql`
  query getAgentProfile2026($contactId: Int!) {
    contactByContactId(contactId: $contactId) {
      id
      agentIdV2
      agencyId
      name
      agencyName
      agencyUrlPath
      jobCategory
      jobTitle
      hasReinsw
      profileUrl
      recommendations {
        numberOfReviews
        overallStarRating
      }
      agencyByAgencyId {
        id
        name
        profileUrl
        address {
          displayAddress
          suburb {
            id
            suburbId
            name
          }
        }
        branding {
          id
          backgroundColour
          logo {
            url
          }
        }
      }
      address {
        displayAddress
        suburb {
          id
          suburbId
          name
          slug
        }
      }
      statistics {
        totalForSale
        totalSoldAndAuctioned
        totalForRent
        totalLeased
        averageDaysOnMarket
        averageSoldPrice
      }
    }
  }
`;

const STATS_CAVEAT = 'Based on data from the past 12 months';

const AgentProfile2026 = ({
  contactId,
  isEmbeddedApp = false,
  starRatingsEnabled = false,
  hideStarRatings = false,
}: AgentProfile2026Props): JSX.Element => {
  const { data, loading, error } = useQuery<GetAgentProfile2026Query>(
    AGENT_PROFILE_2026_QUERY,
    { variables: { contactId } },
  );
  const contact = data?.contactByContactId;
  const { agentIdV2, agencyId } = contact || {};
  const agency = contact?.agencyByAgencyId;

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (!loading && (!data || !agentIdV2 || !agencyId)) {
    throw new Error('Insufficient data to render Agent Profile page.');
  }

  const breadcrumbs = mapContactBreadcrumbs({
    baseUrl: publicRuntimeConfig.BASE_URL,
    profileUrl: contact?.profileUrl?.split('/').filter(Boolean).pop(),
    name: contact?.name ?? undefined,
    suburbName: contact?.address?.suburb?.name ?? undefined,
    suburbSlug: contact?.address?.suburb?.slug ?? undefined,
    statistics: contact?.statistics,
  });

  const agencyUrl = buildAgencyProfileUrl(
    agency?.profileUrl ?? contact?.agencyUrlPath,
    agencyId,
  );
  const bannerProps =
    contact && agencyUrl
      ? {
          agencyName: agency?.name ?? contact.agencyName ?? '',
          agencyUrl,
          address:
            agency?.address?.displayAddress ??
            contact.address?.displayAddress ??
            '',
          suburb:
            agency?.address?.suburb?.name ??
            contact.address?.suburb?.name ??
            '',
          logoUrl: agency?.branding?.logo?.url,
          brandColour: agency?.branding?.backgroundColour,
        }
      : null;

  const totalReviews = contact?.recommendations?.numberOfReviews;
  const overallStarRating = contact?.recommendations?.overallStarRating;
  const shouldShowRating =
    starRatingsEnabled &&
    !hideStarRatings &&
    !!overallStarRating &&
    !!totalReviews;
  const overviewProps = contact
    ? {
        agentName: contact.name ?? '',
        jobTitle: contact.jobTitle,
        jobCategory: contact.jobCategory,
        agencyName: contact.agencyName,
        agencyUrl: buildAgencyProfileUrl(
          contact.agencyUrlPath,
          contact.agencyId,
        ),
        overallStarRating: shouldShowRating ? overallStarRating : undefined,
        totalReviews: shouldShowRating ? totalReviews : undefined,
        isReinswMember: !!contact.hasReinsw,
      }
    : null;

  return (
    <div>
      {!isEmbeddedApp && (
        <>
          <Header
            {...mapHeaderData({
              baseUrl: publicRuntimeConfig.BASE_URL,
              breadcrumbsData: null,
            })}
          />
          <div css={styles.breadcrumbBar}>
            <div css={[styles.wrapper, styles.breadcrumbs]}>
              <Breadcrumbs
                baseUrl={publicRuntimeConfig.BASE_URL}
                breadcrumbs={breadcrumbs}
                backButtonLabel="Back"
              />
            </div>
          </div>
        </>
      )}
      <main id={skipLinkName} data-testid="trade-profile-2026">
        <div css={styles.mainContainer}>
          {bannerProps && <AgentProfileAgencyBanner {...bannerProps} />}
          <div
            css={styles.bodyContainer}
            data-testid="agent-profile-2026__hero"
          >
            {overviewProps && (
              <div css={styles.details}>
                <AgentProfileOverview {...overviewProps} />
              </div>
            )}
            <div css={styles.tiles}>
              <StatTiles
                variant={getStatTilesVariant(contact?.jobCategory)}
                statistics={contact?.statistics ?? {}}
                caveat={STATS_CAVEAT}
              />
            </div>
            <div css={styles.sidebar}>
              <div css={styles.photo}>
                <Placeholder label="Agent photo" />
              </div>
              <div css={styles.sidebarContact}>
                <Placeholder label="Contact card" minHeight={225} />
              </div>
            </div>
            <div css={styles.content}>
              <div css={styles.divider} />
              <Placeholder label="Section_MyProperties" minHeight={970} />
              <Placeholder label="Section_MyTrackRecord" minHeight={714} />
              <Placeholder label="Section_About" minHeight={779} />
              <div id={COMPONENT_IDS.recommendations}>
                <Placeholder label="Section_Reviews" minHeight={1353} />
              </div>
              <div css={styles.stackedContact}>
                <Placeholder label="Contact card" minHeight={100} />
              </div>
            </div>
          </div>
        </div>
      </main>
      {!isEmbeddedApp && agentIdV2 && agencyId && (
        <ContactProfileFooterWithFragment
          agentIdV2={agentIdV2}
          agencyId={agencyId}
        />
      )}
    </div>
  );
};

export default AgentProfile2026;
